/**
 * Helpers for resilient uploads on unreliable networks (e.g. satellite / congested links).
 * Used by direct multipart uploads to the backend API.
 */

/** Default: 1 try + 3 retries after transient failures */
export const DEFAULT_UPLOAD_MAX_ATTEMPTS = 4

/** Base delay before first retry; grows exponentially (capped) */
export const DEFAULT_UPLOAD_BASE_DELAY_MS = 1200

export async function fetchUploadBearerToken(): Promise<string | null> {
  const tokenResponse = await fetch('/api/auth/upload-token')
  if (!tokenResponse.ok) return null
  const body = await tokenResponse.json().catch(() => ({}))
  const token = body?.token
  return typeof token === 'string' ? token : null
}

export function isTransientUploadHttpStatus(status: number): boolean {
  return (
    status === 408 ||
    status === 429 ||
    status === 500 ||
    status === 502 ||
    status === 503 ||
    status === 504
  )
}

export async function sleep(ms: number): Promise<void> {
  await new Promise<void>((resolve) => setTimeout(resolve, ms))
}

export function backoffMsForAttempt(attemptIndex: number, baseMs: number): number {
  const exponential = Math.min(baseMs * 2 ** attemptIndex, 25_000)
  const jitter = Math.floor(Math.random() * 450)
  return exponential + jitter
}

export type ErrorWithStatus = Error & { status?: number }

export function isErrorWithStatus(e: unknown): e is ErrorWithStatus {
  return e instanceof Error && 'status' in e && typeof (e as ErrorWithStatus).status === 'number'
}

/**
 * Run an async upload action with retries on transient failures.
 * Caller throws ErrorWithStatus for HTTP failures so `status` can inform retry policy.
 */
export async function executeWithUploadRetries<T>(
  action: () => Promise<T>,
  shouldRetry: (error: unknown, attemptIndex: number) => boolean,
  options?: { maxAttempts?: number; baseDelayMs?: number }
): Promise<T> {
  const maxAttempts = options?.maxAttempts ?? DEFAULT_UPLOAD_MAX_ATTEMPTS
  const baseDelayMs = options?.baseDelayMs ?? DEFAULT_UPLOAD_BASE_DELAY_MS
  let lastError: unknown

  for (let attempt = 0; attempt < maxAttempts; attempt++) {
    try {
      return await action()
    } catch (e) {
      lastError = e
      const retry = shouldRetry(e, attempt) && attempt < maxAttempts - 1
      if (!retry) throw e
      await sleep(backoffMsForAttempt(attempt, baseDelayMs))
    }
  }

  throw lastError
}

/** Default retry rule: network failures, transient HTTP status, and one retry path for 401 (fresh token next attempt). */
export function defaultUploadShouldRetry(error: unknown, _attemptIndex: number): boolean {
  if (error instanceof TypeError && error.message === 'Failed to fetch') return true
  if (isErrorWithStatus(error)) {
    const s = error.status ?? 0
    if (s === 401) return true
    if (isTransientUploadHttpStatus(s)) return true
  }
  return false
}
